import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { fastenerToolingMappingSchema, type FastenerToolingMappingFormData } from "@/types/admin/fastenerToolingMapping.types";
import {
  useFastenerToolingMappings,
  useFastenerToolingMapping,
  useCreateFastenerToolingMapping,
  useUpdateFastenerToolingMapping,
  useDeleteFastenerToolingMapping,
} from "@/hooks/useFastenerToolingMapping";
import type { FastenerToolingMapping } from "@/api/fastenerToolingMapping";

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: FastenerToolingMapping } | null;

const blank: FastenerToolingMappingFormData = { partNo: "", toolingSetId: "" };

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

export const FastenerToolingMappingPage = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, search: search || undefined }),
    [pageIndex, pageSize, search],
  );

  const { data, isLoading, isFetching, isError, error } = useFastenerToolingMappings(params);
  const rows: FastenerToolingMapping[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<FastenerToolingMapping | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const editingKey =
    modal?.mode !== "add" && modal?.item
      ? { partNo: modal.item.partNo, toolingSetId: modal.item.toolingSetId }
      : null;
  const detail = useFastenerToolingMapping(editingKey);

  const createMutation = useCreateFastenerToolingMapping();
  const updateMutation = useUpdateFastenerToolingMapping();
  const deleteMutation = useDeleteFastenerToolingMapping();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FastenerToolingMappingFormData>({
    resolver: zodResolver(fastenerToolingMappingSchema),
  });

  const openAdd = () => { setFormError(null); reset(blank); setModal({ mode: "add" }); };
  const fillForm = (item: FastenerToolingMapping) => {
    reset({ partNo: item.partNo, toolingSetId: item.toolingSetId });
  };
  const openEdit = (item: FastenerToolingMapping) => { setFormError(null); fillForm(item); setModal({ mode: "edit", item }); };
  const openView = (item: FastenerToolingMapping) => { setFormError(null); fillForm(item); setModal({ mode: "view", item }); };
  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: FastenerToolingMappingFormData) => {
    setFormError(null);
    const body = { partNo: form.partNo.trim(), toolingSetId: form.toolingSetId.trim() };

    if (modal?.mode === "edit" && modal.item) {
      updateMutation.mutate(
        {
          oldKey: { partNo: modal.item.partNo, toolingSetId: modal.item.toolingSetId },
          body,
        },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save mapping.")),
        },
      );
    } else {
      createMutation.mutate(body, {
        onSuccess: closeModal,
        onError: (e) => setFormError(apiErrorMessage(e, "Failed to create mapping.")),
      });
    }
  };

  const onConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { partNo: deleteTarget.partNo, toolingSetId: deleteTarget.toolingSetId },
      {
        onSettled: () => setDeleteTarget(null),
      },
    );
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<FastenerToolingMapping>[] = [
    { header: "Partname", key: "partNo" },
    { header: "ToolingSetId", key: "toolingSetId" },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = {
    add: "Add Fastener -> Tooling Mapping record",
    edit: "Edit Fastener -> Tooling Mapping record",
    view: "View Fastener -> Tooling Mapping record",
  };

  return (
    <>
      <PageHeader title="Dataset: Fastener -> Tooling Mapping" subtitle={`Record-level editor for fastener → tooling-set mappings. · ${total} records`} action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load mappings{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search fastener (partname)…"
      />

      <AdminTable columns={cols} rows={rows} keyExtractor={(i) => `${i.partNo}::${i.toolingSetId}`} loading={isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-2xl"
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}
            <PennField label="Partname" htmlFor="partNo" required={!isView} error={!isView ? errors.partNo?.message : undefined}>
              <PennInput id="partNo" disabled={isView} error={!isView && !!errors.partNo} {...register("partNo")} />
            </PennField>
            <PennField label="Tooling Set Id" htmlFor="toolingSetId" required={!isView} error={!isView ? errors.toolingSetId?.message : undefined}>
              <PennInput id="toolingSetId" disabled={isView} error={!isView && !!errors.toolingSetId} {...register("toolingSetId")} />
            </PennField>
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={onConfirmDelete}
        itemLabel={deleteTarget ? `${deleteTarget.partNo} → ${deleteTarget.toolingSetId}` : undefined}
      />
    </>
  );
};
